import WarningAlert from '@/components/Alert/WarningAlert';
import UploadFileDropzone from '@/components/common/UploadFileDropzone';
import type { UploadFile } from '@/components/common/UploadFileToFolder';
import { buildRpuFileName } from '@/components/Ingestions/rpuFileName';
import RpuUploadFormLoader from '@/components/Ingestions/RpuUploadFormLoader';
import { useGetSusQuery } from '@/features/api/endpoints/suEndpoints';
import type { SUSRow } from '@/models/sus.type';
import { formatFileSize } from '@/shared/utils/file.utils';
import { FILE_TYPES } from '@/shared/utils/upload-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Code, Select, Stack, Text } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useTranslate } from '@tolgee/react';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

const SUS_FIELDS = ['id', 'nom', 'finess_geo', 'numero_ordre', 'date_gel'] as const;
const DATE_FORMAT = 'L';
// Le flow plafonne la taille de page ; les SU accessibles tiennent largement dans cette limite.
const SUS_PAGE_SIZE = 1000;
const ACCEPTED_FILE_TYPES = { ...FILE_TYPES.xml, ...FILE_TYPES.csv, ...FILE_TYPES.txt };
const MAX_FILE_SIZE = 30 * 1024 * 1024;

type Props = {
  /** Identifiant Keycloak (`sub`) de l'utilisateur, repris dans le nom du fichier */
  userId: string;
  onUpload: UploadFile;
};

const formSchema = z.object({
  susId: z.number(),
  file: z.instanceof(File),
});

type FormValues = z.infer<typeof formSchema>;

/** Seuls les SU rattachés à un établissement (FINESS géographique renseigné) sont proposés. */
const hasFiness = (sus: SUSRow): boolean => Boolean(sus.finess_geo?.trim());

/** Le gel est actif dès que sa date est atteinte (une date future n'est pas encore active). */
const isFrozen = (dateGel: string | null | undefined): dateGel is string =>
  Boolean(dateGel) && !dayjs(dateGel).isAfter(dayjs(), 'day');

/**
 * Dépôt d'un fichier RPU : choix du service d'urgence et du fichier, puis envoi sous le nom
 * normalisé, affiché avant l'envoi.
 */
const RpuUploadForm = ({ userId, onUpload }: Props) => {
  const { t } = useTranslate();
  // Horodatage du nom de fichier, figé à la sélection : le nom affiché est celui envoyé.
  const [selectedAt, setSelectedAt] = useState<Date | null>(null);

  const { data, isLoading, isError } = useGetSusQuery({
    page: 1,
    pageSize: SUS_PAGE_SIZE,
    fields: SUS_FIELDS,
    orderBy: 'nom asc',
  });

  const susList = useMemo(() => (data?.rows ?? []).filter(hasFiness), [data]);

  const susOptions = useMemo(
    () =>
      susList.map(({ id, nom, finess_geo }) => ({
        value: String(id),
        label: `${nom} (${finess_geo})`,
      })),
    [susList],
  );

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { isSubmitting, errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      susId: undefined as unknown as number,
      file: undefined as unknown as File,
    },
  });

  // Un seul SU accessible : il est sélectionné par défaut.
  useEffect(() => {
    const [onlySus] = susList;
    if (susList.length === 1 && getValues('susId') === undefined) {
      setValue('susId', onlySus.id, { shouldValidate: true });
    }
  }, [susList, getValues, setValue]);

  const susId = useWatch({ control, name: 'susId' });
  const file = useWatch({ control, name: 'file' });
  const selectedSus = susList.find(({ id }) => id === susId);
  const frozenSince = isFrozen(selectedSus?.date_gel) ? selectedSus.date_gel : null;

  const fileName =
    selectedSus && file && selectedAt
      ? buildRpuFileName({
          finessGeo: selectedSus.finess_geo,
          numeroOrdre: selectedSus.numero_ordre,
          userId,
          originalName: file.name,
          date: selectedAt,
        })
      : null;

  // L'erreur d'envoi précédente ne vaut plus dès que le fichier ou le SU change.
  const handleFileSelected = (selectedFile: File) => {
    clearErrors('root.server');
    setSelectedAt(new Date());
    setValue('file', selectedFile, { shouldValidate: true });
  };

  const onSubmit = async (values: FormValues) => {
    if (!fileName) return;
    const errorKey = await onUpload(values.file, fileName);
    if (errorKey) {
      setError('root.server', { message: errorKey });
    }
  };

  const serverErrorKey = errors.root?.server?.message;

  if (isLoading) {
    return <RpuUploadFormLoader />;
  }

  if (isError) {
    return (
      <Text c="red" role="alert">
        {t('common.filters.sus.error')}
      </Text>
    );
  }

  if (susList.length === 0) {
    return <Text role="status">{t('admin.ingestions.import.sus.none')}</Text>;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack gap="md">
        <Controller
          control={control}
          name="susId"
          render={({ field, fieldState }) => (
            <Select
              label={t('admin.ingestions.import.sus')}
              placeholder={t('admin.ingestions.import.sus.placeholder')}
              data={susOptions}
              value={field.value != null ? String(field.value) : null}
              onChange={value => {
                clearErrors('root.server');
                field.onChange(value != null ? Number(value) : undefined);
              }}
              onBlur={field.onBlur}
              searchable
              nothingFoundMessage={t('common.filters.sus.empty')}
              error={fieldState.error ? t('admin.ingestions.import.sus.required') : undefined}
              disabled={isSubmitting}
              required
            />
          )}
        />
        {frozenSince && (
          <WarningAlert>
            {t('admin.ingestions.import.sus.frozen', {
              date: dayjs(frozenSince).format(DATE_FORMAT),
            })}
          </WarningAlert>
        )}
        <Controller
          control={control}
          name="file"
          render={({ fieldState }) => (
            <Stack gap={4}>
              <UploadFileDropzone
                accept={ACCEPTED_FILE_TYPES}
                maxSize={MAX_FILE_SIZE}
                onFileSelected={handleFileSelected}
              />
              {file && (
                <Text size="sm">
                  {t('admin.ingestions.import.selectedFile', {
                    name: file.name,
                    size: formatFileSize(file.size),
                  })}
                </Text>
              )}
              {fieldState.error && (
                <Text size="sm" c="red.6" role="alert">
                  {t('admin.ingestions.import.file.required')}
                </Text>
              )}
            </Stack>
          )}
        />
        <div aria-live="polite">
          {fileName && (
            <Stack gap={4}>
              <Text size="sm">{t('admin.ingestions.import.fileName')}</Text>
              <Code block>{fileName}</Code>
            </Stack>
          )}
        </div>
        {serverErrorKey && (
          <Alert color="red" variant="light" role="alert" icon={<IconAlertCircle />}>
            {t(serverErrorKey)}
          </Alert>
        )}
        <Button type="submit" loading={isSubmitting}>
          {t('file.submission.submit')}
        </Button>
      </Stack>
    </form>
  );
};

export default RpuUploadForm;
